<template>
    <div class="tabs">
        <ul class="nav nav-tabs" role="tablist">
            <li
                v-for="(tab, index) in tabs"
                :key="tab.id"
                class="nav-item"
                role="presentation"
            >
                <button
                    :id="`${tab.id}-tab`"
                    :ref="(el) => (tabButtons[index] = el)"
                    type="button"
                    role="tab"
                    class="nav-link"
                    :class="{ active: index === activeIndex }"
                    :aria-controls="tab.id"
                    :aria-selected="index === activeIndex ? 'true' : 'false'"
                    :tabindex="index === activeIndex ? undefined : -1"
                    @click="activate(index)"
                    @keydown="onKeydown($event, index)"
                >
                    {{ tab.title }}
                </button>
            </li>
        </ul>
        <div class="tab-content">
            <slot />
        </div>
    </div>
</template>

<script setup>
import {computed, nextTick, provide, shallowReactive} from 'vue';
import {baseTabsKey} from './baseTabsKey';

const activeIndex = defineModel({type: Number, default: 0});

const tabs = shallowReactive([]);
const tabButtons = [];

const activeTabId = computed(() => tabs[activeIndex.value]?.id ?? null);

provide(baseTabsKey, {
    activeTabId,
    register(tab) {
        tabs.push(tab);
    },
    unregister(tab) {
        const index = tabs.indexOf(tab);
        if (index !== -1) {
            tabs.splice(index, 1);
        }
    },
});

function activate(index) {
    activeIndex.value = index;
}

// WAI-ARIA tabs pattern: arrow keys cycle through tabs, Home/End jump to the edges.
async function onKeydown(event, index) {
    const lastIndex = tabs.length - 1;
    let targetIndex;

    switch (event.key) {
        case 'ArrowLeft':
            targetIndex = index > 0 ? index - 1 : lastIndex;
            break;
        case 'ArrowRight':
            targetIndex = index < lastIndex ? index + 1 : 0;
            break;
        case 'Home':
            targetIndex = 0;
            break;
        case 'End':
            targetIndex = lastIndex;
            break;
        default:
            return;
    }

    event.preventDefault();
    activate(targetIndex);
    await nextTick();
    tabButtons[targetIndex]?.focus();
}
</script>
